import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Container from "../hoc/Container";
import { useOtherStoreReferralTracking } from "../visoria/useReferralTracking";
import {
  nintendo_img,
  playStation_img,
  steam_img,
  youtube_img,
  vk_img,
  telegram_img,
  catalog_nintendo,
  catalog_steam,
  catalog_playstation,
} from "../../images";
import { attachStoreListImages } from "../visoria/helpers";
import { OTHER_STORE_LIST } from "../visoria/stores";

const otherStoreCards = attachStoreListImages(OTHER_STORE_LIST);

const CATALOG_CARDS = [
  {
    id: "nintendo",
    to: "/nintendo-discount",
    image: catalog_nintendo,
    alt: "Каталог игр Nintendo eShop",
    title: "Игры и скидки Nintendo",
    text: "Скидки eShop и цены в рублях",
  },
  {
    id: "steam",
    to: "/steam-discount",
    image: catalog_steam,
    alt: "Каталог игр Steam",
    title: "Игры и скидки Steam",
    text: "Скидки Steam и пополнение под игру",
  },
  {
    id: "playstation",
    to: "/playstation-discount",
    image: catalog_playstation,
    alt: "Каталог игр PlayStation Store",
    title: "Игры и скидки PlayStation",
    text: "Скидки PS Store и цены в рублях",
  },
];

const FAQ = [
  {
    q: "Как пополнить Nintendo eShop из России?",
    a: "Выберите карту eShop США ($) или Польши (zl) и номинал, оплатите через СБП — код придёт сразу после оплаты. Активируйте его на аккаунте того же региона: на Switch через «Ввести код» или на сайте Nintendo.",
    link: { to: "/nintendo-eshop/shop", label: "Пополнить Nintendo eShop" },
  },
  {
    q: "Как пополнить Steam из России и стран СНГ?",
    a: "Укажите логин Steam и сумму в рублях, оплатите через СБП — деньги зачислятся на кошелёк, без кодов. Подходят аккаунты России, Беларуси, Казахстана, Армении, Узбекистана и других стран СНГ; на российский аккаунт приходит ровно указанная сумма.",
    link: { to: "/steam", label: "Пополнить Steam" },
  },
  {
    q: "Как пополнить PlayStation Store из России?",
    a: "Выберите карту PSN Польши (zl) или Индии (INR) и номинал, оплатите через СБП — код придёт сразу после оплаты. Активируйте его на аккаунте того же региона: на PS5, PS4 или на сайте PlayStation.",
    link: { to: "/playstation/shop", label: "Пополнить PlayStation Store" },
  },
  {
    q: "Карта какого региона мне подойдёт?",
    a: "Карта пополнения работает только в магазине своего региона: карта США — для аккаунта США, польская — для польского. Steam пополняется по логину для аккаунтов России и СНГ. Регион указан в настройках аккаунта Nintendo, PlayStation или Steam.",
  },
  {
    q: "Как узнать, сколько стоит игра в рублях?",
    a: "В каталоге игр и скидок цены Nintendo eShop, Steam и PlayStation Store пересчитаны в рубли по курсу наших карт. Там же видно, какие коды нужны для покупки, — их можно сразу оплатить.",
    link: { href: "/nintendo/igry", label: "Все игры Nintendo с ценами в рублях" },
  },
  {
    q: "Как оплатить и когда придёт код?",
    a: "Оплата через СБП — по QR-коду или через эквайринг банка. Код приходит сразу после оплаты, ждать не нужно.",
  },
  {
    q: "Какие ещё сервисы можно оплатить?",
    a: "В разделе «Другие магазины» — карты Apple, Xbox и Game Pass, Spotify, Fortnite, Roblox, Riot, PUBG, Nintendo Switch Online, а также карты Nintendo, Steam и PlayStation других регионов.",
    link: { href: "#other-stores", label: "Другие магазины", scroll: true },
  },
  {
    q: "Можно ли доверять магазину?",
    a: "Trendy shop — магазин блогера trendy_eng: 75 000+ подписчиков на YouTube, 16 000+ во ВКонтакте и 10 000+ в Telegram. Реальные отзывы покупателей — в нашей теме ВКонтакте.",
    link: { href: "https://vk.com/topic-213291589_48811365", label: "Читать отзывы", external: true },
  },
];

const FAQ_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
}).replace(/</g, "\\u003c");

function FaqLink({ link }) {
  const label = `${link.label} →`;
  if (link.to) {
    return (
      <Link to={link.to} className="faq-link">
        {label}
      </Link>
    );
  }
  return (
    <a
      href={link.href}
      className="faq-link"
      onClick={link.scroll ? scrollToOtherStores : undefined}
      {...(link.external ? { target: "_blank", rel: "noreferrer" } : {})}
    >
      {label}
    </a>
  );
}

const SCROLL_CANCEL_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"];
let cancelSlowScroll = null;

/** Медленнее встроенного smooth: по пути к «Другим магазинам» видно каталог игр. */
function slowScrollTo(targetY) {
  cancelSlowScroll?.();
  const startY = window.scrollY;
  const distance = targetY - startY;
  const duration = Math.min(1800, Math.max(800, Math.abs(distance) * 0.7));
  const startedAt = performance.now();
  let frame = 0;
  const cancel = () => {
    cancelAnimationFrame(frame);
    SCROLL_CANCEL_EVENTS.forEach((type) => window.removeEventListener(type, cancel));
    if (cancelSlowScroll === cancel) cancelSlowScroll = null;
  };
  cancelSlowScroll = cancel;
  SCROLL_CANCEL_EVENTS.forEach((type) =>
    window.addEventListener(type, cancel, { passive: true }),
  );
  const step = (now) => {
    const t = Math.min(1, (now - startedAt) / duration);
    window.scrollTo(0, startY + distance * (0.5 - Math.cos(Math.PI * t) / 2));
    if (t < 1) frame = requestAnimationFrame(step);
    else cancel();
  };
  frame = requestAnimationFrame(step);
}

function scrollToOtherStores(event) {
  if (scrollToOtherStoresSection()) event.preventDefault();
}

function scrollToOtherStoresSection() {
  const target = document.getElementById("other-stores");
  if (!target) return false;
  const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
  const rect = target.getBoundingClientRect();
  const free = window.innerHeight - rect.height;
  const centered = window.matchMedia?.("(min-width: 768px)").matches && free > margin * 2;
  const offset = centered ? free / 2 : margin;
  const maxY = document.documentElement.scrollHeight - window.innerHeight;
  const targetY = Math.max(0, Math.min(maxY, rect.top + window.scrollY - offset));
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
    window.scrollTo(0, targetY);
  } else {
    slowScrollTo(targetY);
  }
  return true;
}

function GlobeIcon() {
  return (
    <svg
      className="other-stores-cta-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
    </svg>
  );
}

function MainPage() {
  useOtherStoreReferralTracking();
  const location = useLocation();
  useEffect(() => {
    window.ym(93997310, "hit", "/", {
      params: {
        title: "Main Page",
      },
    });
  }, []);
  useEffect(() => {
    if (location.hash !== "#other-stores") {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      return undefined;
    }
    const timer = setTimeout(scrollToOtherStoresSection, 50);
    return () => clearTimeout(timer);
  }, [location.key, location.hash]);

  return (
    <Container>
      <section className="main-content-block main-stack">
        <article>
          <h1 className="main-title">
            Коды Nintendo eShop и PlayStation,
            <br />
            пополнение Steam
          </h1>
          <div className="flex justify-center">
            <p className="main-text main-text-small main-text-centered w-full text-gray-700 dark:text-white">
              Добро пожаловать в магазин блогера trendy_eng! Здесь можно легко и
              удобно купить подарочные карты Nintendo eShop, Steam и PlayStation,
              чтобы пополнить баланс и покупать игры. Всегда в наличии коды
              Nintendo Switch, Steam (PC, Steam Deck), PlayStation 4 и PlayStation 5
              (PSN). Оплата через эквайринг или QR, моментальная доставка.
            </p>
          </div>
          <div className="mt-2">
            <div className="main-grid main-grid-shops">
              <div className="main-card">
                <Link
                  to="/nintendo-eshop/shop"
                  className="main-card-link group"
                  aria-label="Купить карту пополнения Nintendo eShop"
                >
                  <img
                    className="shop-img-large image-glow glow-nintendo transition duration-200 ease-out group-hover:scale-[1.02] group-active:scale-[0.99]"
                    src={nintendo_img}
                    alt="Карта пополнения Nintendo eShop"
                    width="720"
                    height="483"
                    fetchpriority="high"
                  />
                </Link>
                <div className="main-card-info">
                  <h2 className="main-card-title">Пополнить Nintendo</h2>
                  <p className="main-muted">Польша 🇵🇱 (zl) · США 🇺🇸 ($)</p>
                </div>
              </div>
              <div className="main-card">
                <Link
                  to={"/steam"}
                  className="main-card-link group"
                  aria-label="Пополнить кошелёк Steam"
                >
                  <img
                    className="shop-img-large image-glow glow-steam transition duration-200 ease-out group-hover:scale-[1.02] group-active:scale-[0.99]"
                    src={steam_img}
                    alt="Пополнение кошелька Steam"
                    width="720"
                    height="483"
                    fetchpriority="high"
                  />
                </Link>
                <div className="main-card-info">
                  <h2 className="main-card-title">Пополнить Steam</h2>
                  <p className="main-muted">Страны СНГ (RU, KZ и др.)</p>
                </div>
              </div>
              <div className="main-card">
                <Link
                  to={"/playstation"}
                  className="main-card-link group"
                  aria-label="Купить карту пополнения PlayStation Store"
                >
                  <img
                    className="shop-img-large image-glow glow-psn transition duration-200 ease-out group-hover:scale-[1.02] group-active:scale-[0.99]"
                    src={playStation_img}
                    alt="Карта пополнения PlayStation Store"
                    width="720"
                    height="483"
                    fetchpriority="high"
                  />
                </Link>
                <div className="main-card-info">
                  <h2 className="main-card-title">Пополнить PlayStation</h2>
                  <p className="main-muted">Польша 🇵🇱 (zl) · Индия 🇮🇳 (INR)</p>
                </div>
              </div>
            </div>
          </div>
          <div className="other-stores-cta-wrap">
            <a
              href="#other-stores"
              onClick={scrollToOtherStores}
              className="other-stores-cta"
            >
              <span className="other-stores-cta-inner">
                <GlobeIcon />
                <span>Другие магазины и регионы</span>
                <span className="other-stores-cta-chevron" aria-hidden="true">
                  ↓
                </span>
              </span>
            </a>
            <p className="other-stores-cta-note">
              Apple, Fortnite, Xbox, Roblox и другие
            </p>
          </div>
        </article>
        <div className="main-divider" />
        <section className="catalog-section">
          <h2 className="main-subtitle catalog-title">Больше, чем каталог игр</h2>
          <p className="catalog-lead">
            Десятки тысяч игр Nintendo, Steam и PlayStation, свежие скидки и цены
            в рублях.{" "}
            <span className="catalog-lead-accent">
              Выбрали игру&nbsp;— купили за пару минут.
            </span>
          </p>
          <div className="catalog-grid">
            {CATALOG_CARDS.map((card) => (
              <Link
                key={card.to}
                to={card.to}
                className={`catalog-card catalog-card-${card.id}`}
              >
                <div className="catalog-card-media">
                  <img
                    className="catalog-card-img"
                    src={card.image}
                    alt={card.alt}
                    width="960"
                    height="643"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="catalog-card-body">
                  <div className="min-w-0">
                    <h3 className="catalog-card-title">{card.title}</h3>
                    <p className="catalog-card-text">{card.text}</p>
                  </div>
                  <span className="catalog-card-arrow" aria-hidden="true">
                    →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
        <div className="main-divider" />
        <section id="other-stores" className="main-other-stores-section">
          <h2 className="main-subtitle">Другие магазины</h2>
          <p className="main-other-stores-lead">
            Отдельный каталог подарочных карт — другие регионы и сервисы. Это не
            те же магазины Nintendo, Steam и PlayStation, что выше.
          </p>
          <div className="main-grid main-grid-regions">
            {otherStoreCards.map((item) => (
              <div key={item.id} className="region-card-wrap">
                <Link
                  to={`/store/${item.id}`}
                  className="region-card group"
                  aria-label={item.label}
                >
                  <div className={`region-card-visual ${item.bgClass}`}>
                    {item.image ? (
                      <img
                        className="region-card-img"
                        src={item.image}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : null}
                    <span className="region-card-label">{item.label}</span>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </section>
        <div className="main-divider" />
        <address>
          <h2 className="main-subtitle main-subtitle-social">
            Наши социальные сети - комментарии всегда открыты
          </h2>
          <div className="flex flex-row flex-wrap items-center justify-center gap-2 md:gap-4">
            <div className="social-item">
              <a
                to={"https://www.youtube.com/channel/UCdfv-fZKGIobmzVxHX9mSAA"}
                href="https://www.youtube.com/channel/UCdfv-fZKGIobmzVxHX9mSAA"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => window.ym(93997310, "reachGoal", "youTubeClick")}
                className="main-social-link group"
                aria-label="Trendy Shop YouTube"
              >
                <img
                  className="social-img transition duration-200 ease-out group-hover:scale-105 group-active:scale-95"
                  src={youtube_img}
                  alt="Trendy Shop YouTube"
                  loading="lazy"
                  decoding="async"
                />
              </a>
              <div className="social-text">
                <span className="social-title">YouTube</span>
                <span>75 000+</span>
                <span>подписчиков</span>
              </div>
            </div>
            <div className="social-item">
              <a
                href="https://t.me/trendy_eng"
                target="_blank"
                rel="noopener noreferrer"
                className="main-social-link group"
                aria-label="Trendy Shop Telegram"
              >
                <img
                  className="social-img transition duration-200 ease-out group-hover:scale-105 group-active:scale-95"
                  src={telegram_img}
                  alt="Trendy Shop Telegram"
                  loading="lazy"
                  decoding="async"
                  onClick={() => window.ym(93997310, "reachGoal", "tgClick")}
                />
              </a>
              <div className="social-text">
                <span className="social-title">Telegram</span>
                <span>10 000+</span>
                <span>подписчиков</span>
              </div>
            </div>
            <div className="social-item">
              <a
                href="https://vk.com/trendy_eng"
                target="_blank"
                rel="noopener noreferrer"
                className="main-social-link group"
                aria-label="Trendy Shop Вконтакте"
              >
                <img
                  className="social-img transition duration-200 ease-out group-hover:scale-105 group-active:scale-95"
                  src={vk_img}
                  alt="Trendy Shop VK"
                  loading="lazy"
                  decoding="async"
                  onClick={() => window.ym(93997310, "reachGoal", "vkClick")}
                />
              </a>
              <div className="social-text">
                <span className="social-title">Вконтакте</span>
                <span>16 000+</span>
                <span>подписчиков</span>
              </div>
            </div>
          </div>
        </address>
        <div className="main-divider" />
        <section className="reviews-section">
          <h2 className="main-subtitle main-subtitle-social">
            Отзывы покупателей
          </h2>
          <p className="reviews-text">
            Посмотреть реальные отзывы можно в нашей теме ВК.
          </p>
          <a
            href="https://vk.com/topic-213291589_48811365"
            target="_blank"
            rel="noreferrer"
            className="reviews-link"
          >
            Читать отзывы
          </a>
        </section>
        <div className="main-divider" />
        <section className="faq-section" aria-labelledby="faq-title">
          <h2 id="faq-title" className="main-subtitle">
            Частые вопросы
          </h2>
          <div className="faq-list">
            {FAQ.map((item) => (
              <details key={item.q} className="faq-item">
                <summary className="faq-question">
                  <h3 className="faq-question-text">{item.q}</h3>
                  <span className="faq-chevron" aria-hidden="true" />
                </summary>
                <div className="faq-answer">
                  <p>{item.a}</p>
                  {item.link ? <FaqLink link={item.link} /> : null}
                </div>
              </details>
            ))}
          </div>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: FAQ_LD }}
          />
        </section>
      </section>
    </Container>
  );
}

export default MainPage;
